import { Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import LosFileSelect from '../../components/common/LosFileSelect'
import LosFormInputField from '../../components/common/LosFormInputField'
import LosTextField from '../../components/common/LosTextField'
import LosAppBar from '../../components/LosAppBar'
import { LosFormButtonView } from '../../components/LosFormButton'
import LosFormContainer from '../../components/LosFormContainer'
import { useLos } from '../../providers/LosProvider'
import {
  CustomerApplication,
  LosStep,
  PartnershipFormData,
} from '../../types/Los'
import { StringUtility } from '../../utils/StringUtility'
import { useLosAuth } from '../../providers/LosAuthProvider'
import VerifyGSTDetailsDialog from '../../components/VerifyGSTDetailsDialog'
import VerificationSuccessDialog from '../../components/VerificationSuccessDialog'
import { LosService } from '../../api/LosService'
import { LocalStorageUtility, StorageKeys } from '../../utils/StorageUtility'

enum InputType {
  COMPANY_NAME,
  GST_NUMBER,
  PARTNERSHIP_FIRM_DEED,
  GST_DOCUMENT,
}

function PartnershipFirmCompanyDetails() {
  const {
    navigateToNextStep,
    currentStepMetaData,
    saveCompanyData,
    customerApplication,
    fetchPanDetailsForPanInfoId,
    updateGstStatusToAccepted,
    currentStep,
  } = useLos()
  const { isAuthenticated } = useLosAuth()
  const [formData, setFormData] = useState<PartnershipFormData>({
    companyName: '',
    gstNo: '',
  })
  const [errors, setErrors] = useState<Partial<PartnershipFormData>>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [openVerifyGSTDialog, setOpenVerifyGSTDialog] = useState<boolean>(false)
  const [openGstVerifiedDialog, setOpenGstVerifiedDialog] =
    useState<boolean>(false)

  function onGstVerificationSuccessClick() {
    navigateToNextStep()
  }

  function handleUpdate(type: InputType, value: string) {
    const updatedErrors: Partial<PartnershipFormData> = { ...errors }
    switch (type) {
      case InputType.COMPANY_NAME:
        if (value.length <= 0) {
          updatedErrors.companyName = 'Company Name is required'
        } else {
          delete updatedErrors.companyName
        }
        setFormData(prev => {
          return {
            ...prev,
            companyName: value,
          }
        })
        break
      case InputType.GST_NUMBER:
        if (value.length <= 0) {
          updatedErrors.gstNo = 'GST Number is required'
        } else {
          const isValid = StringUtility.validateGSTNumber(value)
          if (isValid == false) {
            updatedErrors.gstNo = 'Valid GST Number is required'
          } else {
            delete updatedErrors.gstNo
          }
        }
        setFormData(prev => {
          return {
            ...prev,
            gstNo: value,
          }
        })
        break
    }
    setErrors(updatedErrors)
  }

  const isGSTNumberInputValid = () => {
    if (!!errors.gstNo) return false
    return (formData.gstNo ?? '').length > 0
  }

  const isInputValid = () => {
    let isGstNumber = isGSTNumberInputValid()
    let isNameValid = (formData.companyName ?? '').length > 0
    return isGstNumber && isNameValid
  }

  async function onSubmit() {
    try {
      setLoading(true)
      await saveCompanyData(formData)
      navigateToNextStep()
      setOpenVerifyGSTDialog(true)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  async function onGstVerificationConfirm() {
    try {
      setLoading(true)
      const response = await updateGstStatusToAccepted()
      setOpenGstVerifiedDialog(true)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  async function fetchCompanyDetails() {
    try {
      setLoading(true)
      const customerApplication =
        LocalStorageUtility.getItem<CustomerApplication>(
          StorageKeys.CUSTOMER_APPLICATION
        )
      const customerApplicationId = customerApplication?.id

      if (!customerApplicationId) return

      const response = await LosService.getUserDetails(customerApplicationId)
      setFormData(prev => {
        return {
          ...prev,
          companyName: response.companyDetailsResponse.companyName,
          gstNo: response.companyDetailsResponse.gstNo,
        }
      })
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  async function handleGstAcceptedState() {
    // const companyPanId = customerApplication?.companyPanId
    const companyPanId = customerApplication?.companyDetails.panId
    if (companyPanId) {
      setOpenVerifyGSTDialog(true)
    }
    setLoading(false)
  }

  function runAfterDelay(callback: () => void, delay: number) {
    setTimeout(callback, delay)
  }

  useEffect(() => {
    fetchCompanyDetails()
  }, [customerApplication])

  useEffect(() => {
    if (currentStep == LosStep.GST_ACCEPTED && isAuthenticated) {
      setLoading(true)
      runAfterDelay(handleGstAcceptedState, 500)
    }
  }, [currentStep, customerApplication, isAuthenticated])

  return (
    <LosFormContainer
      renderSubmitButton={() => {
        return (
          <LosFormButtonView
            text='Verify GST'
            onClick={onSubmit}
            disabled={isInputValid() == false}
            loading={loading}
          />
        )
      }}
      renderAppBar={() => {
        return (
          <LosAppBar
            totalSteps={currentStepMetaData.total}
            currentStep={currentStepMetaData.index + 1}
            currentStepLabel={currentStepMetaData.categoryName}
          />
        )
      }}
    >
      <Grid container rowSpacing={'1.5rem'}>
        <Grid item xs={12}>
          <LosFormInputField label='Company Name'>
            <LosTextField
              value={formData.companyName}
              disabled
              onChange={e =>
                handleUpdate(InputType.COMPANY_NAME, e.target.value)
              }
              error={!!errors.companyName}
              helperText={errors.companyName}
            />
          </LosFormInputField>
        </Grid>

        <Grid item xs={12}>
          <LosFormInputField label='GST NO.'>
            <LosTextField
              value={formData.gstNo}
              onChange={e => handleUpdate(InputType.GST_NUMBER, e.target.value)}
              error={!!errors.gstNo}
              helperText={errors.gstNo}
              isValid={isGSTNumberInputValid()}
            />
          </LosFormInputField>
        </Grid>
      </Grid>
      <VerifyGSTDetailsDialog
        gstNumber={''}
        open={openVerifyGSTDialog}
        onClose={() => setOpenVerifyGSTDialog(false)}
        onSubmit={onGstVerificationConfirm}
      />
      <VerificationSuccessDialog
        open={openGstVerifiedDialog}
        onClose={function (): void {
          setOpenGstVerifiedDialog(false)
        }}
        onSubmit={onGstVerificationSuccessClick}
        title={'Verify GST'}
        description={'Your Gst has been verified'}
        buttonText={'Proceed'}
      />
    </LosFormContainer>
  )
}

export default PartnershipFirmCompanyDetails
